import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { ClipFilters, ClipSortKey, ClipView } from "./types"

export function ClipsSearchDialog({
  open,
  onOpenChange,
  initialQuery,
  filters,
  sort,
  view,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialQuery: string
  filters: ClipFilters
  sort: ClipSortKey
  view: ClipView
}) {
  const [query, setQuery] = React.useState(initialQuery)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      setQuery(initialQuery)
      // Focus on next tick so the input mounts first.
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, initialQuery])

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    router.get("/clips", { ...filters, sort, view, q: query }, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
    onOpenChange(false)
  }

  function handleClear() {
    setQuery("")
    router.get("/clips", { ...filters, sort, view, q: "" }, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Search clips</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            ref={inputRef}
            type="search"
            placeholder="Search by title, folder, or short description…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <p className="text-xs text-ink-muted">
            Tip: prefix-token matching across title, folder path, and short description. Try multiple words.
          </p>
          <DialogFooter>
            {initialQuery && (
              <Button type="button" variant="ghost" onClick={handleClear}>Clear current</Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">Search</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Listens globally for the "/" key and invokes `onTrigger`. Ignores the
// keystroke when an input/textarea/contentEditable element is focused so it
// doesn't hijack typing in form fields.
export function useSearchHotkey(onTrigger: () => void) {
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "/") return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (!target) return
      const tag = target.tagName
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable
      if (isEditable) return
      e.preventDefault()
      onTrigger()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onTrigger])
}
